<template>
  <!-- Only when it matters: from 80% of the contact limit, and a way out instead of a dead end -->
  <Message v-if="quota && close" :severity="reached ? 'error' : 'info'" :closable="false">
    <div class="flex flex-col gap-1">
      <span class="font-medium tabular-nums">
        {{ t('mail_limit_line', { contacts: num(quota.contacts), limit: num(quota.contact_limit) }) }}
      </span>
      <span>{{ reached ? t('mail_limit_reached_why') : t('mail_limit_close_why') }}</span>
      <span v-if="requestedAt" class="text-sm">
        <AppIcon icon="circle-check" class="mr-1" />{{ t('mail_limit_requested', { date: date(requestedAt) }) }}
      </span>
      <Button v-else-if="canWrite" :label="t('mail_limit_request')" size="small" :severity="reached ? undefined : 'secondary'"
        :loading="requesting" class="self-start mt-1" @click="request" />
      <span v-if="requestError" class="text-sm font-medium" role="alert">{{ requestError }}</span>
    </div>
  </Message>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';

export type Quota = { contacts: number; contact_limit: number; sent_this_month: number; monthly_limit: number };

const props = defineProps<{ quota: Quota | null }>();
const { t } = useI18n();
const userStore = useUserStore();
const { settings, loadSettings, action } = useMail();
const { num, date } = useLocaleFormat();

const canWrite = computed(() => userStore.hasRight('mail.write'));
// Sending stops above the limit (mail_campaign_schedule: contacts > contact_limit)
const reached = computed(() => !!props.quota && props.quota.contacts > props.quota.contact_limit);
const close = computed(() => !!props.quota && props.quota.contact_limit > 0 && props.quota.contacts >= props.quota.contact_limit * 0.8);
const requestedAt = computed(() => settings.value?.contact_limit_requested_at ?? null);

onMounted(() => { if (!settings.value) loadSettings(); });

const requesting = ref(false);
const requestError = ref('');
const request = async () => {
  requesting.value = true;
  requestError.value = '';
  try {
    const { requested_at } = await action<{ requested_at: string }>('limit_request');
    if (settings.value) settings.value = { ...settings.value, contact_limit_requested_at: requested_at };
  } catch (e) {
    console.error(e);
    requestError.value = t('mail_limit_request_failed');
  } finally {
    requesting.value = false;
  }
};
</script>
